import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  OFFER_LINK_HEADER,
  type CandidateDeclineRequest,
  type CandidateOfferView,
  type DecideOfferStepRequest,
  type OfferApprovalItem,
  type OfferDetailView,
  type OfferLetterPreview,
  type OfferLetterPreviewRequest,
  type OfferLetterRequest,
  type OfferLetterView,
  type OfferListRow,
  type OfferPreviewChoice,
  type OfferTermsRequest,
  type RecordAnswerRequest,
  type WithdrawOfferRequest,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost, apiPut, apiUrl, request } from '../../../lib/api-client';

/**
 * S23 — OFR-01–03, ADMIN-16 and the candidate's page. The server's realtime hints name
 * `offers` and `offer-approvals`, so an offer already open updates the moment an approver
 * decides or the candidate answers. Every change answers with the whole offer, which
 * replaces the cache in one step.
 */

export const OFFERS_KEY = ['offers'] as const;
export const OFFER_APPROVALS_KEY = ['offer-approvals'] as const;
export const OFFER_LETTERS_KEY = ['admin', 'offer-letters'] as const;

export function offerKey(id: string): readonly unknown[] {
  return [...OFFERS_KEY, 'detail', id];
}

export function useOffer(id: string): UseQueryResult<OfferDetailView, ApiError> {
  return useQuery({
    queryKey: offerKey(id),
    queryFn: () => apiGet<OfferDetailView>(`/offers/${id}`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export function useJobOffers(jobId: string): UseQueryResult<readonly OfferListRow[], ApiError> {
  return useQuery({
    queryKey: [...OFFERS_KEY, 'job', jobId],
    queryFn: () => apiGet<readonly OfferListRow[]>(`/jobs/${jobId}/offers`),
    placeholderData: (previous) => previous,
  });
}

/** OFR-02 — offer steps waiting on the viewer. */
export function useOfferApprovals(isEnabled = true): UseQueryResult<readonly OfferApprovalItem[], ApiError> {
  return useQuery({
    queryKey: OFFER_APPROVALS_KEY,
    queryFn: () => apiGet<readonly OfferApprovalItem[]>('/offers/approvals'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const offersApi = {
  saveTerms: (id: string, input: OfferTermsRequest) => apiPut<OfferDetailView>(`/offers/${id}/terms`, input),
  submit: (id: string, version: number) => apiPost<OfferDetailView>(`/offers/${id}/submit`, { version }),
  send: (id: string, version: number) => apiPost<OfferDetailView>(`/offers/${id}/send`, { version }),
  negotiate: (id: string, version: number) => apiPost<OfferDetailView>(`/offers/${id}/negotiate`, { version }),
  sendRevision: (id: string, version: number) => apiPost<OfferDetailView>(`/offers/${id}/send-revision`, { version }),
  answer: (id: string, input: RecordAnswerRequest) => apiPost<OfferDetailView>(`/offers/${id}/answer`, input),
  withdraw: (id: string, input: WithdrawOfferRequest) => apiPost<OfferDetailView>(`/offers/${id}/withdraw`, input),
  noShow: (id: string, version: number) => apiPost<OfferDetailView>(`/offers/${id}/no-show`, { version }),
  copyMessage: (id: string) => apiPost<{ readonly text: string }>(`/offers/${id}/copy-message`, {}),
  uploadSignedCopy: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiPost<OfferDetailView>(`/offers/${id}/signed-copy`, form);
  },
  decide: (stepId: string, input: DecideOfferStepRequest) => apiPost<OfferDetailView>(`/offers/approval-steps/${stepId}/decide`, input),
};

/** Files open in a new tab: the session cookie carries the access (same origin). */
export const offerFiles = {
  preview: (id: string) => `/api/v1/offers/${id}/preview`,
  version: (id: string, number: number) => `/api/v1/offers/${id}/versions/${number}/pdf`,
  signedCopy: (id: string) => `/api/v1/offers/${id}/signed-copy`,
};

// --- ADMIN-16 and step 7 ----------------------------------------------------------------------

/** `isEnabled` false for someone who may not open ADMIN-16: nothing is asked of the server (review W15). */
export function useOfferLetters(isEnabled = true): UseQueryResult<readonly OfferLetterView[], ApiError> {
  return useQuery({ queryKey: OFFER_LETTERS_KEY, queryFn: () => apiGet<readonly OfferLetterView[]>('/admin/offer-letters'), enabled: isEnabled });
}

export const offerLettersApi = {
  create: (input: OfferLetterRequest) => apiPost<readonly OfferLetterView[]>('/admin/offer-letters', input),
  update: (id: string, version: number, letter: OfferLetterRequest) => apiPatch<readonly OfferLetterView[]>(`/admin/offer-letters/${id}`, { version, letter }),
};

/** "Someone else saved first" (the `version` check). */
const HTTP_CONFLICT = 409;

export type SaveOfferLetter = { readonly id: null; readonly letter: OfferLetterRequest } | { readonly id: string; readonly version: number; readonly letter: OfferLetterRequest };

/** Creates or saves a letter; the answer is the whole list, which replaces the cache. A refused save fetches theirs. */
export function useSaveOfferLetter() {
  const queryClient = useQueryClient();
  return useMutation<readonly OfferLetterView[], ApiError, SaveOfferLetter>({
    mutationFn: (save) => (save.id === null ? offerLettersApi.create(save.letter) : offerLettersApi.update(save.id, save.version, save.letter)),
    onSuccess: (letters) => {
      queryClient.setQueryData(OFFER_LETTERS_KEY, letters);
    },
    onError: async (error) => {
      if (error.status === HTTP_CONFLICT) await queryClient.invalidateQueries({ queryKey: OFFER_LETTERS_KEY, exact: true });
    },
  });
}

const OFFER_LETTER_PREVIEW_KEY = [...OFFER_LETTERS_KEY, 'preview'] as const;

/**
 * ADMIN-16's A4 preview (Admin redesign 2026-11-02): the letter as typed, filled from a real offer or from sample
 * values, as the page the PDF prints. The last page stays up while the next is drawn (CLAUDE.md §5a rule 2).
 */
export function useOfferLetterPreview(input: OfferLetterPreviewRequest | null): UseQueryResult<OfferLetterPreview, ApiError> {
  return useQuery({
    queryKey: [...OFFER_LETTER_PREVIEW_KEY, input],
    queryFn: () => apiPost<OfferLetterPreview>('/admin/offer-letters/preview', input),
    enabled: input !== null,
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

/** "Preview with": offers the viewer may open, this letter's own first. */
export function useOfferPreviewChoices(letterId: string | null, isEnabled: boolean): UseQueryResult<readonly OfferPreviewChoice[], ApiError> {
  return useQuery({
    queryKey: [...OFFER_LETTER_PREVIEW_KEY, 'offers', letterId],
    queryFn: () => apiGet<readonly OfferPreviewChoice[]>('/admin/offer-letters/preview-offers', { query: letterId ? { letterId } : {} }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

/**
 * The same preview as the real PDF, stamped DRAFT, opened from memory in a new tab and never stored. The tab opens on
 * the click itself, so a pop-up blocker lets it through; the file follows.
 */
export async function openOfferLetterPreviewPdf(input: OfferLetterPreviewRequest): Promise<void> {
  const tab = window.open('', '_blank');
  if (!tab) throw new ApiError({ code: 'popup_blocked', message: 'Allow pop-ups for this page to open the PDF.', status: 0 });
  tab.opener = null;
  const response = await fetch(apiUrl('/admin/offer-letters/preview/pdf'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    tab.close();
    const payload: unknown = await response.json().catch(() => null);
    const message = isErrorPayload(payload) ? payload.error.message : "The PDF couldn't be made. Try again in a moment.";
    throw new ApiError({ code: 'preview_failed', message, status: response.status });
  }
  tab.location.href = URL.createObjectURL(await response.blob());
}

function isErrorPayload(value: unknown): value is { readonly error: { readonly message: string } } {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const error: unknown = value.error;
  return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';
}


export interface OfferChoices {
  readonly letters: readonly { readonly id: string; readonly name: string }[];
  readonly chains: readonly { readonly id: string; readonly name: string }[];
}

export function useOfferChoices(ventureId: string): UseQueryResult<OfferChoices, ApiError> {
  return useQuery({
    queryKey: ['offer-choices', ventureId],
    queryFn: () => apiGet<OfferChoices>('/offer-choices', { query: { ventureId } }),
  });
}

// --- The candidate's page (D16: no account; the link travels in a header) ----------------------

export function candidateOfferKey(token: string): readonly unknown[] {
  return ['offer-response', token];
}

function linkHeaders(token: string): Readonly<Record<string, string>> {
  return { [OFFER_LINK_HEADER]: token };
}

export function useCandidateOffer(token: string): UseQueryResult<CandidateOfferView, ApiError> {
  return useQuery({
    queryKey: candidateOfferKey(token),
    queryFn: async () => (await request<CandidateOfferView>('/offer-response', { headers: linkHeaders(token) })).data,
    enabled: token !== '',
    retry: (count, error) => error.status === 0 && count < 3,
  });
}

export const candidateOfferApi = {
  accept: async (token: string) => (await request<CandidateOfferView>('/offer-response/accept', { method: 'POST', headers: linkHeaders(token) })).data,
  decline: async (token: string, input: CandidateDeclineRequest) => (await request<CandidateOfferView>('/offer-response/decline', { method: 'POST', body: input, headers: linkHeaders(token) })).data,
  /** The PDF, fetched with the header (never in the address) and handed to the browser to save. */
  downloadPdf: async (token: string, fileName: string): Promise<void> => {
    const response = await fetch('/api/v1/offer-response/pdf', { headers: linkHeaders(token) });
    if (!response.ok) throw new Error("The letter couldn't be downloaded. Try again in a moment.");
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};
